import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Zap, ArrowLeft, Activity } from 'lucide-react'

export default function TrafficAnalysisPage() {
  const navigate = useNavigate()
  const [protocols] = useState([
    { name: 'HTTP/HTTPS', packets: 156000, bytes: 2.3, percentage: 35, trend: 'up' },
    { name: 'DNS', packets: 89000, bytes: 0.8, percentage: 18, trend: 'stable' },
    { name: 'SMTP', packets: 45000, bytes: 1.2, percentage: 12, trend: 'down' },
    { name: 'SSH', packets: 34000, bytes: 0.5, percentage: 10, trend: 'stable' },
    { name: 'FTP', packets: 28000, bytes: 3.1, percentage: 15, trend: 'up' },
    { name: 'Other', packets: 18000, bytes: 0.6, percentage: 10, trend: 'stable' },
  ])

  const [flows] = useState([
    { source: '192.168.1.50', destination: '8.8.8.8', protocol: 'DNS', packets: 5432, bytes: 0.4 },
    { source: '192.168.1.100', destination: '1.1.1.1', protocol: 'HTTPS', packets: 12543, bytes: 2.1 },
    { source: '192.168.1.75', destination: '208.67.222.222', protocol: 'DNS', packets: 4231, bytes: 0.3 },
    { source: '192.168.1.200', destination: '103.21.244.0', protocol: 'HTTP', packets: 8956, bytes: 0.9 },
  ])

  const stats = useMemo(() => {
    const totalPackets = protocols.reduce((sum, p) => sum + p.packets, 0)
    const totalBytes = protocols.reduce((sum, p) => sum + p.bytes, 0)
    return { totalPackets, totalBytes }
  }, [protocols])

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate('/network')} className="flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-6 font-semibold">
          <ArrowLeft size={20} /> Back to Network Management
        </button>

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Traffic Analysis</h1>
          <p className="text-slate-400">Deep packet inspection and network traffic analysis</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Total Packets</div>
            <div className="text-3xl font-bold text-blue-400">{(stats.totalPackets / 1000).toFixed(0)}K</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Total Data</div>
            <div className="text-3xl font-bold text-green-400">{stats.totalBytes.toFixed(1)} GB</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Active Flows</div>
            <div className="text-3xl font-bold text-purple-400">{flows.length}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <h3 className="text-lg font-bold text-white mb-6">Protocol Distribution</h3>
            <div className="space-y-4">
              {protocols.map((proto, idx) => (
                <div key={idx}>
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-semibold text-sm">{proto.name}</span>
                      <span className={`text-xs font-bold ${proto.trend === 'up' ? 'text-red-400' : proto.trend === 'down' ? 'text-green-400' : 'text-yellow-400'}`}>
                        {proto.trend === 'up' ? '↑' : proto.trend === 'down' ? '↓' : '→'}
                      </span>
                    </div>
                    <span className="text-cyan-400 font-semibold text-sm">{proto.bytes} GB</span>
                  </div>
                  <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-cyan-500"
                      style={{ width: `${proto.percentage}%` }}
                    ></div>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{proto.percentage}% • {proto.packets.toLocaleString()} packets</div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <h3 className="text-lg font-bold text-white mb-6">Top Traffic Flows</h3>
            <div className="space-y-3">
              {flows.map((flow, idx) => (
                <div key={idx} className="bg-slate-700/30 rounded-lg p-4 border border-blue-500/10">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="text-white font-semibold text-sm">{flow.source} → {flow.destination}</p>
                      <p className="text-slate-400 text-xs mt-1">Protocol: <span className="text-blue-400">{flow.protocol}</span></p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-cyan-400">{flow.packets.toLocaleString()} packets</span>
                    <span className="text-green-400">{flow.bytes} GB</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
          <h3 className="text-lg font-bold text-white mb-4">Analysis Insights</h3>
          <div className="space-y-3">
            <div className="bg-blue-600/10 border border-blue-500/30 rounded-lg p-4 flex gap-3">
              <Activity className="text-blue-400 flex-shrink-0" size={20} />
              <p className="text-blue-300 text-sm">HTTP/HTTPS traffic accounts for 35% of all traffic. Consider implementing aggressive caching policies.</p>
            </div>
            <div className="bg-yellow-600/10 border border-yellow-500/30 rounded-lg p-4 flex gap-3">
              <Activity className="text-yellow-400 flex-shrink-0" size={20} />
              <p className="text-yellow-300 text-sm">FTP traffic is consuming 15% bandwidth. Evaluate migration to more efficient protocols like SFTP.</p>
            </div>
            <div className="bg-green-600/10 border border-green-500/30 rounded-lg p-4 flex gap-3">
              <Activity className="text-green-400 flex-shrink-0" size={20} />
              <p className="text-green-300 text-sm">DNS queries are optimized. No unusual activity detected in this protocol.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
